import {BehaviorSubject, combineLatest, Subject} from 'rxjs';
import {skipUntil, switchMap, map} from 'rxjs/operators';
import {get} from 'svelte/store';
import {timeUtil} from '@/helper/time';
import {mc} from '@/core';
import {operationApi} from '@/api/operation';
import {assetsApi} from '@/api/assets';
import {investmentApi} from '@/api/investment';
import {launchpoolApi} from '@/api/launch-pool';
import {marginTradeApi} from '@/api/margin-trade';
import {fiatAccountApi} from '@/api/fiat-account';
import {lang$} from '@/store/lang';
import {browser$} from '@/streams/browser';
import {currenciesCache$} from '@/streams/config/currency';
import {HistoryQueryUtaState} from '@/modules/assets/const';
import {formatterUpgradedBeforeAfter} from './util';

export const loading$ = new Subject();
export const query$ = new Subject();
export const walletsDetail$ = new BehaviorSubject({total: 0, rows: []});

export const queryFiatAccount$ = new Subject();
export const fiatAccount$ = new BehaviorSubject({total: 0, rows: []});

export const loadMarginTrade$ = new Subject();
export const marginTradeDetails$ = new BehaviorSubject({total: 0, rows: []});

export const contractQuery$ = new Subject();
const accountFunds$ = new BehaviorSubject({total: 0, rows: []});
export const accountDetail$ = combineLatest([accountFunds$, currenciesCache$]).pipe(map(convertData));

export const loadBanner$ = new Subject();
export const bannerDetail$ = new BehaviorSubject({total: 0, rows: []});

export const loadLaunchPoolInvest$ = new Subject();
export const launchPoolInvestList$ = new BehaviorSubject({total: 0, rows: []});

function convertData([data, currenciesCache]) {
  const {rows, total} = data;
  const list = convertAccountFunds(rows, currenciesCache);
  return {rows: list, total};
}

export function convertAccountFunds(rows, currenciesCache) {
  return rows.map((item) => {
    const {displayCurrency} = currenciesCache.has(item.currency) ? currenciesCache.get(item.currency) : {displayCurrency: item.currency};
    return {
      createdTime: timeUtil.formatDateTime(item.createTime),
      account: displayCurrency,
      type: item.type,
      typeCode: item.type,
      bonusExtra: item.bonusExtra,
      changeAmount: item.amountRv,
      balance: item.balanceRv,
    };
  });
}

query$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadWalletsDetail(query))
  )
  .subscribe((x) => walletsDetail$.next(x));

queryFiatAccount$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadFiatAccount(query))
  )
  .subscribe((x) => fiatAccount$.next(x));

loadMarginTrade$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadMarginTradeDetails(query))
  )
  .subscribe((x) => marginTradeDetails$.next(x));

contractQuery$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadAccountDetail(query))
  )
  .subscribe();

loadBanner$
  .pipe(
    skipUntil(browser$),
    switchMap(() => loadContractBanner())
  )
  .subscribe((x) => bannerDetail$.next(x));

loadLaunchPoolInvest$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadLaunchpoolList(query))
  )
  .subscribe((x) => launchPoolInvestList$.next(x));

async function loadWalletsDetail({coin, bizCode, offset}) {
  loading$.next(true);
  const query = {
    currency: coin,
    offset,
    limit: 10,
    start: '',
    end: '',
    bizCode,
    side: '',
    withCount: true,
  };
  const {data, error} = await assetsApi.getWalletsDetail(query);
  loading$.next(false);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadFiatAccount({currency, bizCode, current, limit}) {
  loading$.next(true);
  const query = {
    fiatCurrency: currency,
    pageNum: current,
    pageSize: limit || 10,
    bizType: bizCode,
  };
  const {data, error} = await fiatAccountApi.fiatBalanceChangeQuery(query);
  loading$.next(false);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadMarginTradeDetails({coin, bizCode, limit, current, utaState = HistoryQueryUtaState.beforeUpgrade, targetFund = 0}) {
  // utaState:  0：升级前的流水，1：升级后的流水，默认值0
  // targetFund:  0： 交易账户，1：资金账户，如果未升级不传即可，默认值0
  loading$.next(true);
  const query = {
    currency: coin,
    pageNum: current,
    pageSize: limit,
    utaState,
    targetFund,
    bizCode,
    start: '',
    end: '',
  };
  const {data, error} = await marginTradeApi.getFundsHistory(query);
  loading$.next(false);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadAccountDetail({currency, offset, type, utaState = 0}) {
  loading$.next(true);
  const query = {
    currency,
    offset,
    type,
    limit: 10,
    start: 1,
    end: Date.now() + 86400000,
    bizCode: '',
    side: '',
    withCount: true,
  };

  // 升级前后记录区分
  const query2 = formatterUpgradedBeforeAfter(
    utaState,
    query,
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      end: unifiedAccountUpgradedTime,
    }),
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      start: unifiedAccountUpgradedTime,
    })
  );

  const {data, error} = await assetsApi.tradeAccountDetailV2(query2);
  loading$.next(false);
  if (data) {
    accountFunds$.next(data);
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
}

export const journalQuery$ = new Subject();
export const accountJournal$ = new BehaviorSubject({total: 0, rows: []});

journalQuery$
  .pipe(
    skipUntil(browser$),
    switchMap((query) => loadAccountJournal(query))
  )
  .subscribe((x) => accountJournal$.next(x));

async function loadAccountJournal({coin, eventTypes, offset, utaState = HistoryQueryUtaState.beforeUpgrade}) {
  loading$.next(true);
  const query = {
    currency: coin,
    offset,
    limit: 10,
    eventTypes,
    withCount: true,
  };

  // 升级前后记录区分
  const query2 = formatterUpgradedBeforeAfter(
    utaState,
    query,
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      end: unifiedAccountUpgradedTime,
    }),
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      start: unifiedAccountUpgradedTime,
    })
  );

  const {data, error} = await investmentApi.getAccountFlow(query2);
  loading$.next(false);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadContractBanner() {
  const query = {itemKey: 'banner', location: 4, lang: get(lang$)};
  const {data, error} = await operationApi.getOperationBannersLogin(query);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}

async function loadLaunchpoolList({offset, investCurrency, tradeType, utaState = HistoryQueryUtaState.beforeUpgrade}) {
  loading$.next(true);
  const query = {
    offset,
    limit: 10,
    tradeType,
    investCurrency,
    withCount: true,
  };

  // 升级前后记录区分
  const query2 = formatterUpgradedBeforeAfter(
    utaState,
    query,
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      end: unifiedAccountUpgradedTime,
    }),
    (p, unifiedAccountUpgradedTime) => ({
      ...p,
      start: unifiedAccountUpgradedTime,
    })
  );

  const {data, error} = await launchpoolApi.getLaunchPoolFunds(query2);
  loading$.next(false);
  if (data) {
    return data;
  }
  if (error) {
    mc.send('toast', 'error', error);
  }
  return {total: 0, rows: []};
}
